import { useState } from "preact/hooks";
import type { CrmData } from "../../types";
import { Drawer } from "../ui/Modal";
import { Badge, InvoiceStatusBadge } from "../ui/Badge";
import { Avatar } from "../ui/Avatar";
import { store } from "../../store/store";
import { toast } from "../shell/Toast";
import { RecordPaymentModal } from "./RecordPaymentModal";
import { canRecordPayment, canSendInvoice, canVoidInvoice, computeInvoiceTotals, effectiveInvoiceStatus, isHighValueInvoice } from "../../lib/billing";
import { formatDate, money } from "../../lib/format";

export function InvoiceDrawer({
  invoiceId,
  data,
  now,
  onClose,
  onOpenCompany,
  onOpenOpportunity,
}: {
  invoiceId: string;
  data: CrmData;
  now: Date;
  onClose: () => void;
  onOpenCompany: (companyId: string) => void;
  onOpenOpportunity: (opportunityId: string) => void;
}) {
  const [paying, setPaying] = useState(false);

  const invoice = data.invoices.find((i) => i.id === invoiceId);
  if (!invoice) return null;

  const company = data.companies.find((c) => c.id === invoice.companyId);
  const opportunity = invoice.opportunityId ? data.opportunities.find((o) => o.id === invoice.opportunityId) : undefined;
  const totals = computeInvoiceTotals(invoice);
  const status = effectiveInvoiceStatus(invoice, now);
  const highValue = isHighValueInvoice(invoice);

  function send() {
    const check = canSendInvoice(invoice!);
    if (!check.allowed) {
      toast.error(check.reason ?? "This invoice cannot be sent.");
      return;
    }
    store.sendInvoice(invoice!.id);
    toast.success(`${invoice!.invoiceNumber} sent to ${company?.name ?? "the customer"}.`);
  }

  function voidInvoice() {
    const check = canVoidInvoice(invoice!);
    if (!check.allowed) {
      toast.error(check.reason ?? "This invoice cannot be voided.");
      return;
    }
    store.voidInvoice(invoice!.id);
    toast.success(`${invoice!.invoiceNumber} has been voided.`);
  }

  function openPayment() {
    const check = canRecordPayment(invoice!);
    if (!check.allowed) {
      toast.error(check.reason ?? "You cannot record a payment on this invoice.");
      return;
    }
    setPaying(true);
  }

  return (
    <div>
      <Drawer
        onClose={onClose}
        title={
          <div>
            <button type="button" class="drawer__breadcrumb drawer__breadcrumb--link" data-ai-id="open-company" onClick={() => onOpenCompany(invoice.companyId)}>
              <Avatar name={company?.name ?? "Unknown"} size="sm" />
              {company?.name ?? "Unknown company"}
            </button>
            <h2 class="drawer__opp-name">{invoice.invoiceNumber}</h2>
            <div class="drawer__badges">
              <InvoiceStatusBadge status={status} />
              {highValue && <Badge tone="info">High Value</Badge>}
            </div>
          </div>
        }
      >
        <div class="drawer-grid">
          <div class="drawer-grid__stats">
            <div class="stat">
              <span class="stat__label">Total</span>
              <span class="stat__value">{money(totals.total)}</span>
            </div>
            <div class="stat">
              <span class="stat__label">Paid</span>
              <span class="stat__value">{money(invoice.amountPaid)}</span>
            </div>
            <div class="stat">
              <span class="stat__label">Balance</span>
              <span class="stat__value">{money(totals.balance)}</span>
            </div>
            <div class="stat">
              <span class="stat__label">Due date</span>
              <span class="stat__value">
                {formatDate(invoice.dueDate)}
              </span>
            </div>
          </div>

          <section class="drawer-section">
            <h3>Line Items</h3>
            <div class="line-table">
              <div class="line-table__head">
                <span>Description</span>
                <span>Qty</span>
                <span>Unit price</span>
                <span>Amount</span>
              </div>
              {invoice.lineItems.map((li) => (
                <div class="line-table__row" key={li.id}>
                  <span>{li.description}</span>
                  <span class="num">{li.quantity}</span>
                  <span class="num">{money(li.unitPrice)}</span>
                  <span class="num">{money(li.quantity * li.unitPrice)}</span>
                </div>
              ))}
            </div>
            <div class="invoice-totals">
              <div>
                <span>Subtotal</span>
                <span>{money(totals.subtotal)}</span>
              </div>
              <div>
                <span>Tax ({invoice.taxRate}%)</span>
                <span>{money(totals.taxAmount)}</span>
              </div>
              <div class="invoice-totals__grand">
                <span>Total</span>
                <span>{money(totals.total)}</span>
              </div>
            </div>
          </section>

          <section class="drawer-section">
            <h3>Details</h3>
            <dl class="detail-list">
              <div>
                <dt>Issue date</dt>
                <dd>{formatDate(invoice.issueDate)}</dd>
              </div>
              <div>
                <dt>Due date</dt>
                <dd>{formatDate(invoice.dueDate)}</dd>
              </div>
              <div>
                <dt>Paid date</dt>
                <dd>{formatDate(invoice.paidDate)}</dd>
              </div>
              <div>
                <dt>Linked deal</dt>
                <dd>
                  {opportunity ? (
                    <button type="button" class="link-inline" data-ai-id="open-opportunity" onClick={() => onOpenOpportunity(opportunity.id)}>
                      {opportunity.name}
                    </button>
                  ) : (
                    "—"
                  )}
                </dd>
              </div>
            </dl>
            {invoice.notes && (
              <div class="internal-note" style={{ background: "var(--surface-inset)", color: "var(--text-secondary)", borderColor: "var(--border)" }}>
                {invoice.notes}
              </div>
            )}
          </section>

          <section class="drawer-section actions-bar">
            <h3>Actions</h3>
            <div class="row" style={{ flexWrap: "wrap" }}>
              {invoice.status === "Draft" && (
                <button type="button" class="btn btn--primary" data-ai-id="send-invoice" onClick={send}>
                  Send invoice
                </button>
              )}
              {(invoice.status === "Sent" || invoice.status === "Partially Paid") && (
                <button type="button" class="btn btn--primary" data-ai-id="record-payment" onClick={openPayment}>
                  Record payment
                </button>
              )}
              {invoice.status !== "Paid" && invoice.status !== "Void" && (
                <button type="button" class="btn btn--danger" data-ai-id="void-invoice" onClick={voidInvoice}>
                  Void invoice
                </button>
              )}
            </div>
          </section>
        </div>
      </Drawer>

      {paying && <RecordPaymentModal invoice={invoice} onClose={() => setPaying(false)} onDone={() => setPaying(false)} />}
    </div>
  );
}
